import moment from 'moment'
import MedicalReportMixin from '@/shared/mixins/medical-report.mixin'
import { mapGetters, mapActions } from 'vuex'
import IconEllipse from '@/assets/icons/ellipse.svg?raw'
import IconAlarm from '@/assets/icons/alarm.svg?url'
import { getReportTimerMinutesWithFallback } from '@/shared/utils/report-timer-fallback'

export default {
  name: 'report-countdown',
  props: {
    examId: {
      type: Number,
      default: 0,
      medicalReportService: Object
    },
    prioritasUpdateTime: {
      type: String,
      default: ''
    },
    reportTimer: {
      type: Number,
      default: import.meta.env.VITE_MINUTES_TO_REPORT,
      medicalReportService: Object
    }
  },
  watch: {
    prioritasUpdateTime (newVal, oldVal) {
      this.minutes = getReportTimerMinutesWithFallback(this.reportTimer)
      this.restartCountDown()
    },
    reportTimer (newVal) {
      this.minutes = getReportTimerMinutesWithFallback(newVal)
      this.restartCountDown()
    }
  },
  mixins: [
    MedicalReportMixin
  ],
  data () {
    return {
      isVisible: true,
      iconAlarm: IconAlarm,
      iconEllipse: IconEllipse,
      minutes: getReportTimerMinutesWithFallback(this.reportTimer),
      timeRemaining: '',
      interval: Object,
      lastVisibilityChangeTime: Date.now(),
      instanceHash: Math.random().toString(36).substring(7),
      showMiniCountdown: false,
      isTimeout: false,
      isShowingStillReportingPopup: false,
      videoCallPausedAt: null,
      restartingAfterVideoCall: false,
      isRefreshingVideoCallReportTime: false,
      videoCallReportRefreshAfter: 0
    }
  },
  beforeDestroy () {
    this.stopCountDown()
    this.clearVideoCallReportRefresh()
    this.$root.$off('videoCallSession', this.onVideoCallSession)
  },
  mounted () {
    document.addEventListener('visibilitychange', () => this.handleVisibilityChange(this.instanceHash), false)
    this.setTimerHash(this.instanceHash)
    this.startCountDown()
    const observer = new IntersectionObserver((entries) => {
      if (!entries[0].isIntersecting) {
        this.showMiniCountdown = true
      } else {
        this.showMiniCountdown = false
      }
    })
    observer.observe(document.querySelector('#time-remaining'))
    this.$root.$on('refreshCountdownEvent', this.handleVisibilityChange)
    this.onVideoCallSession = (isOpen) => {
      if (isOpen) {
        this.pauseCountdownForVideoCall()
      } else {
        this.resumeCountdownAfterVideoCall()
      }
    }
    this.$root.$on('videoCallSession', this.onVideoCallSession)
  },
  destroyed () {
    document.removeEventListener('visibilitychange', () => this.handleVisibilityChange(this.instanceHash), false)
  },
  methods: {
    ...mapGetters([
      'getTimerHash'
    ]),
    ...mapActions([
      'setTimerHash'
    ]),
    refreshCountDown () {
      this.refreshTimeout()
    },
    getDiff () {
      const lastUpdateOrDefault = new Date(this.prioritasUpdateTime)
      const pTime = moment(lastUpdateOrDefault).add(this.minutes, 'm')
      const now = new Date()
      const diff = moment.duration(pTime.diff(now)).asMilliseconds()
      return diff
    },
    pauseCountdownForVideoCall () {
      if (this.videoCallPausedAt) return
      this.videoCallPausedAt = Date.now()
      this.isShowingStillReportingPopup = false
      this.$root.$emit('pauseStillReportingPopup')
      this.stopCountDown()
      this.scheduleVideoCallReportRefresh()
    },
    async resumeCountdownAfterVideoCall () {
      if (this.restartingAfterVideoCall) return
      this.restartingAfterVideoCall = true
      this.clearVideoCallReportRefresh()
      this.videoCallPausedAt = null
      this.isShowingStillReportingPopup = false
      this.isTimeout = false
      try {
        await this.refreshTimeout()
      } finally {
        this.restartingAfterVideoCall = false
      }
    },
    scheduleVideoCallReportRefresh () {
      this.clearVideoCallReportRefresh()
      if (!this.videoCallPausedAt) return
      const delay = Math.max(this.getDiff() - 15000, 0)
      this.videoCallReportRefreshTimeout = setTimeout(() => {
        this.refreshReportTimeDuringVideoCall()
      }, delay)
    },
    clearVideoCallReportRefresh () {
      clearTimeout(this.videoCallReportRefreshTimeout)
      this.videoCallReportRefreshTimeout = null
    },
    refreshReportTimeOnce () {
      if (!this.reportTimeRefreshInFlight) {
        this.reportTimeRefreshInFlight = this.refreshReportTime(this.examId).finally(() => {
          this.reportTimeRefreshInFlight = null
        })
      }
      return this.reportTimeRefreshInFlight
    },
    async refreshReportTimeDuringVideoCall () {
      if (!this.videoCallPausedAt || this.restartingAfterVideoCall || this.isRefreshingVideoCallReportTime || !this.examId) return
      if (this.videoCallReportRefreshAfter && Date.now() < this.videoCallReportRefreshAfter) return
      this.isRefreshingVideoCallReportTime = true
      try {
        const data = await this.refreshReportTimeOnce()
        if (!this.videoCallPausedAt) return
        this.$root.$emit('refreshTimeout', data.data)
      } catch (error) {
        this.isRefreshingVideoCallReportTime = false
        this.videoCallReportRefreshAfter = Date.now() + 15000
        if (!this.videoCallPausedAt) return
        this.clearVideoCallReportRefresh()
        this.videoCallReportRefreshTimeout = setTimeout(() => {
          this.refreshReportTimeDuringVideoCall()
        }, 15000)
      }
    },
    countDown () {
      if (this.videoCallPausedAt) return
      let diff = this.getDiff()
      this.timeRemaining = moment(diff).format('mm:ss')
      if (this.restartingAfterVideoCall) return
      if (diff <= 0) {
        diff = 0
        this.timeRemaining = moment(diff).format('mm:ss')
        this.stopCountDown()
        this.unassignExam(true)
        this.isTimeout = true
      } else if (diff <= 15000) {
        this.showFeedbackPopup()
      }
    },
    showFeedbackPopup () {
      if (!this.isShowingStillReportingPopup) {
        this.$root.$emit('showFeedbackPopup')
        this.isShowingStillReportingPopup = true
      }
    },
    continueReporting () {
      this.showDialog = false
      this.feedbackShown = false
      this.isShowingStillReportingPopup = false
      this.$root.$emit('refreshCountdownEvent')
    },
    startCountDown () {
      this.countDown()
      this.interval = setInterval(this.countDown, 1000)
    },
    stopCountDown () {
      clearInterval(this.interval)
    },
    getClassTimeout () {
      const minuteRemaining = moment(this.timeRemaining, 'mm:ss').isBefore(moment('01:00', 'mm:ss'))
      const thirtySecondsRemaining = moment(this.timeRemaining, 'mm:ss').isBefore(moment('00:30', 'mm:ss'))
      return {
        'timeout': minuteRemaining,
        'timeout-blink': thirtySecondsRemaining && !this.isTimeout
      }
    },
    async unassignExam (showFeedback) {
      this.$root.$emit('showLoading')
      try {
        const res = await this.hasMoreExams()
        const hasMoreExams = res.data
        if (showFeedback) {
          const data = {
            hasMoreExams,
            type: 'unassign'
          }
          this.$emit('unassignExam', data)
        }
      } finally {
        this.$root.$emit('hideLoading')
      }
    },
    async refreshTimeout () {
      this.$root.$emit('showLoading')

      try {
        const data = await this.refreshReportTimeOnce()
        this.$root.$emit('refreshTimeout', data.data)
        this.restartCountDown()
      } finally {
        this.$root.$emit('hideLoading')
      }
    },
    restartCountDown () {
      this.stopCountDown()
      this.isShowingStillReportingPopup = false
      this.isTimeout = false
      this.isRefreshingVideoCallReportTime = false
      if (this.videoCallPausedAt) {
        this.scheduleVideoCallReportRefresh()
        return
      }
      this.startCountDown()
    },
    getCountDownMillis () {
      return this.getDiff()
    },
    countDownHalfway () {
      const now = moment(new Date())
      const diffLastUpdate = moment.duration(now.diff(this.lastVisibilityChangeTime)).asSeconds()
      const halfTimer = 60 * getReportTimerMinutesWithFallback(this.reportTimer) / 2
      return diffLastUpdate > halfTimer
    },
    canUpdateTimer () {
      const timeRemaining = this.getCountDownMillis()
      return this.countDownHalfway() && timeRemaining > 0
    },
    async handleVisibilityChange () {
      this.isVisible = !document.hidden
      if (this.videoCallPausedAt || this.restartingAfterVideoCall) return
      const currentTimerHash = this.getTimerHash()
      if (this.isVisible && this.canUpdateTimer() && currentTimerHash === this.instanceHash) {
        this.lastVisibilityChangeTime = new Date()
        await this.refreshTimeout()
      }
    }
  }
}
